/**
 * PRODUCT MOCK DATA
 *
 * Centralized mock data for products including list views and records.
 * Uses mockDataManager for localStorage persistence.
 */

import { createPersistedMockData } from '../mockDataManager';
import type { ListView, ListViewRecord } from '../../types/listView';
import type { ProductCatalog } from '../../types/index';

/**
 * DEFAULT PRODUCT LIST VIEWS
 */
export const defaultProductListViews: ListView[] = [
  {
    id: 'lv_products_all',
    name: 'All Products',
    apiName: 'all_products',
    objectApiName: 'Product',
    description: 'All product records',
    isDefault: true,
    columns: [
      { fieldApiName: 'id', fieldLabel: 'Product ID', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'name', fieldLabel: 'Product Name', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'productCode', fieldLabel: 'Product Code', fieldType: 'text', isVisible: true, order: 2 },
      { fieldApiName: 'category', fieldLabel: 'Category', fieldType: 'badge', isVisible: true, order: 3 },
      { fieldApiName: 'unitPrice', fieldLabel: 'Unit Price', fieldType: 'number', isVisible: true, order: 4 },
      { fieldApiName: 'currency', fieldLabel: 'Currency', fieldType: 'text', isVisible: true, order: 5 },
      { fieldApiName: 'isActive', fieldLabel: 'Status', fieldType: 'badge', isVisible: true, order: 6 },
      { fieldApiName: 'createdDate', fieldLabel: 'Created Date', fieldType: 'date', isVisible: true, order: 7 },
    ],
    createdBy: 'System',
    createdDate: '2024-01-01T00:00:00Z'
  },
  {
    id: 'lv_products_active',
    name: 'Active Products',
    apiName: 'active_products',
    objectApiName: 'Product',
    description: 'Active products only',
    isDefault: false,
    columns: [
      { fieldApiName: 'name', fieldLabel: 'Product Name', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'productCode', fieldLabel: 'Product Code', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'category', fieldLabel: 'Category', fieldType: 'badge', isVisible: true, order: 2 },
      { fieldApiName: 'unitPrice', fieldLabel: 'Unit Price', fieldType: 'number', isVisible: true, order: 3 },
      { fieldApiName: 'billingFrequency', fieldLabel: 'Billing Frequency', fieldType: 'text', isVisible: true, order: 4 },
    ],
    filters: [
      { fieldApiName: 'isActive', operator: 'equals', value: true }
    ],
    createdBy: 'Admin',
    createdDate: '2024-02-15T10:30:00Z'
  },
  {
    id: 'lv_products_by_category',
    name: 'By Category',
    apiName: 'products_by_category',
    objectApiName: 'Product',
    description: 'Products grouped by category',
    isDefault: false,
    columns: [
      { fieldApiName: 'name', fieldLabel: 'Product Name', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'category', fieldLabel: 'Category', fieldType: 'badge', isVisible: true, order: 1 },
      { fieldApiName: 'unitPrice', fieldLabel: 'Unit Price', fieldType: 'number', isVisible: true, order: 2 },
      { fieldApiName: 'billingFrequency', fieldLabel: 'Billing Frequency', fieldType: 'text', isVisible: true, order: 3 },
    ],
    createdBy: 'Admin',
    createdDate: '2024-03-10T14:00:00Z'
  }
];

/**
 * DEFAULT PRODUCT RECORDS
 */
export const defaultProductRecords: ProductCatalog[] = [
  {
    id: 'prod_001',
    name: 'Credit Repair',
    productCode: 'CR-001',
    description: 'Comprehensive credit repair service including dispute management and credit score improvement',
    category: 'service',
    isActive: true,
    unitPrice: 99.00,
    currency: 'USD',
    billingFrequency: 'monthly',
    createdDate: '2024-01-01T00:00:00Z',
    lastModifiedDate: '2024-01-01T00:00:00Z',
    createdBy: 'system',
    lastModifiedBy: 'system'
  },
  {
    id: 'prod_002',
    name: 'CyberSweep',
    productCode: 'CS-001',
    description: 'Identity theft protection and data broker removal service',
    category: 'service',
    isActive: true,
    unitPrice: 149.00,
    currency: 'USD',
    billingFrequency: 'monthly',
    createdDate: '2024-01-01T00:00:00Z',
    lastModifiedDate: '2024-01-01T00:00:00Z',
    createdBy: 'system',
    lastModifiedBy: 'system'
  },
  {
    id: 'prod_003',
    name: 'Credit Block',
    productCode: 'CB-001',
    description: 'Credit bureau freeze and monitoring service',
    category: 'service',
    isActive: true,
    unitPrice: 79.00,
    currency: 'USD',
    billingFrequency: 'monthly',
    createdDate: '2024-01-01T00:00:00Z',
    lastModifiedDate: '2024-01-01T00:00:00Z',
    createdBy: 'system',
    lastModifiedBy: 'system'
  },
  {
    id: 'prod_004',
    name: 'Credit Development',
    productCode: 'CD-001',
    description: 'Credit building and development program for individuals with limited credit history',
    category: 'service',
    isActive: true,
    unitPrice: 89.00,
    currency: 'USD',
    billingFrequency: 'monthly',
    createdDate: '2024-01-01T00:00:00Z',
    lastModifiedDate: '2024-01-01T00:00:00Z',
    createdBy: 'system',
    lastModifiedBy: 'system'
  },
  {
    id: 'prod_005',
    name: 'Credit Report (Single)',
    productCode: 'CRPT-001',
    description: 'Single credit report from one bureau',
    category: 'one-time',
    isActive: true,
    unitPrice: 15.00,
    currency: 'USD',
    billingFrequency: 'one-time',
    createdDate: '2024-01-01T00:00:00Z',
    lastModifiedDate: '2024-01-01T00:00:00Z',
    createdBy: 'system',
    lastModifiedBy: 'system'
  },
  {
    id: 'prod_006',
    name: 'Tri-Merge Credit Report',
    productCode: 'CRPT-003',
    description: 'Combined credit report from all three bureaus',
    category: 'one-time',
    isActive: true,
    unitPrice: 35.00,
    currency: 'USD',
    billingFrequency: 'one-time',
    createdDate: '2024-01-01T00:00:00Z',
    lastModifiedDate: '2024-01-01T00:00:00Z',
    createdBy: 'system',
    lastModifiedBy: 'system'
  },
  {
    id: 'prod_007',
    name: 'Credit Monitoring Add-On',
    productCode: 'CM-ADDON',
    description: 'Additional credit monitoring service add-on',
    category: 'addon',
    isActive: true,
    unitPrice: 19.99,
    currency: 'USD',
    billingFrequency: 'monthly',
    createdDate: '2024-01-15T10:00:00Z',
    lastModifiedDate: '2024-01-15T10:00:00Z',
    createdBy: 'admin',
    lastModifiedBy: 'admin'
  },
  {
    id: 'prod_008',
    name: 'Premium Support Package',
    productCode: 'PSP-001',
    description: 'Premium support with dedicated account manager',
    category: 'addon',
    isActive: true,
    unitPrice: 49.99,
    currency: 'USD',
    billingFrequency: 'monthly',
    createdDate: '2024-02-01T09:00:00Z',
    lastModifiedDate: '2024-02-01T09:00:00Z',
    createdBy: 'admin',
    lastModifiedBy: 'admin'
  }
];

/**
 * PERSISTED STORAGE
 */
export const persistedProductListViews = createPersistedMockData(
  'crm_product_list_views',
  defaultProductListViews
);

export const persistedProductRecords = createPersistedMockData(
  'crm_product_records',
  defaultProductRecords
);

/**
 * BACKWARD COMPATIBILITY
 */
export const mockProductListViews = defaultProductListViews;
export const mockProductRecords = defaultProductRecords;
